import React, { createContext, useContext, useEffect, useRef } from "react";
import { API_BASE_ADMIN, STORAGE_KEYS, isCapacitorRuntime } from "../config/vars";

const WebSocketContext = createContext(null);

function getStoredAccessToken() {
  if (!isCapacitorRuntime()) {
    return null;
  }
  try {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  } catch (_) {
    return null;
  }
}

export const WebSocketProvider = ({ children }) => {
  const socketsRef = useRef({}); // room -> WebSocket
  const listenersRef = useRef({}); // room -> {event -> Set(callbacks)}
  const reconnectTimersRef = useRef({});
  const reconnectAttemptsRef = useRef({});
  const roomConfigRef = useRef({});
  const manualCloseRoomsRef = useRef(new Set());

  const clearReconnect = (room) => {
    const timerId = reconnectTimersRef.current[room];
    if (timerId) {
      window.clearTimeout(timerId);
      delete reconnectTimersRef.current[room];
    }
  };

  const hasRoomListeners = (room) => {
    const roomListeners = listenersRef.current[room] || {};
    return Object.values(roomListeners).some((callbacks) => callbacks && callbacks.size > 0);
  };

  const buildWsUrl = (roomType, roomId) => {
    const apiBase = API_BASE_ADMIN;
    if (!apiBase) return null;

    let wsProtocol = "ws";
    let host = "";
    try {
      const u = new URL(apiBase);
      wsProtocol = u.protocol === "https:" ? "wss" : "ws";
      host = u.host;
    } catch (_) {
      wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
      host = apiBase.replace(/^https?:\/\//, "");
    }

    const params = new URLSearchParams();
    const accessToken = getStoredAccessToken();
    if (accessToken) {
      params.set("access_token", accessToken);
    }

    const query = params.toString();
    return `${wsProtocol}://${host}/ws/${roomType}/${roomId}${query ? `?${query}` : ""}`;
  };

  const connect = (roomType, roomId, options = {}) => {
    const { forceReconnect = false } = options;
    const room = `${roomType}:${roomId}`;
    roomConfigRef.current[room] = { roomType, roomId };

    const existing = socketsRef.current[room];
    if (!forceReconnect && existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
      return;
    }

    clearReconnect(room);
    manualCloseRoomsRef.current.delete(room);

    if (existing) {
      try {
        existing.close();
      } catch (_) {
        // ignore
      }
      delete socketsRef.current[room];
    }

    const wsUrl = buildWsUrl(roomType, roomId);
    if (!wsUrl) return;

    const ws = new WebSocket(wsUrl);
    socketsRef.current[room] = ws;

    ws.onopen = () => {
      reconnectAttemptsRef.current[room] = 0;
      clearReconnect(room);
    };

    ws.onerror = (err) => console.error(`[WS] Error on ${room}:`, err);

    ws.onmessage = (msg) => {
      try {
        const { event, payload } = JSON.parse(msg.data);
        const roomListeners = listenersRef.current[room] || {};
        const callbacks = roomListeners[event];
        if (callbacks) {
          Array.from(callbacks).forEach((cb) => cb(payload));
        }
      } catch (err) {
        console.error("[WS] Message parse error:", err);
      }
    };

    ws.onclose = () => {
      if (socketsRef.current[room] === ws) {
        delete socketsRef.current[room];
      }

      if (manualCloseRoomsRef.current.has(room)) {
        manualCloseRoomsRef.current.delete(room);
        clearReconnect(room);
        return;
      }

      if (!hasRoomListeners(room)) {
        clearReconnect(room);
        return;
      }

      if (reconnectTimersRef.current[room]) return;
      const attempt = (reconnectAttemptsRef.current[room] || 0) + 1;
      reconnectAttemptsRef.current[room] = attempt;
      const delayMs = Math.min(1000 * (2 ** (attempt - 1)), 10000);
      reconnectTimersRef.current[room] = window.setTimeout(() => {
        delete reconnectTimersRef.current[room];
        const cfg = roomConfigRef.current[room];
        if (!cfg || !hasRoomListeners(room)) return;
        connect(cfg.roomType, cfg.roomId, { forceReconnect: true });
      }, delayMs);
    };
  };

  const subscribe = (roomType, roomId, event, callback) => {
    const room = `${roomType}:${roomId}`;
    listenersRef.current[room] = listenersRef.current[room] || {};
    listenersRef.current[room][event] = listenersRef.current[room][event] || new Set();
    listenersRef.current[room][event].add(callback);
    if (!socketsRef.current[room]) {
      connect(roomType, roomId);
    }
  };

  const unsubscribe = (roomType, roomId, event, callback) => {
    const room = `${roomType}:${roomId}`;
    if (!listenersRef.current[room]?.[event]) return;
    listenersRef.current[room][event].delete(callback);
    if (listenersRef.current[room][event].size === 0) {
      delete listenersRef.current[room][event];
    }
    if (Object.keys(listenersRef.current[room]).length === 0) {
      delete listenersRef.current[room];
      delete roomConfigRef.current[room];
      delete reconnectAttemptsRef.current[room];
      clearReconnect(room);
      const ws = socketsRef.current[room];
      if (ws) {
        manualCloseRoomsRef.current.add(room);
        delete socketsRef.current[room];
        try {
          ws.close();
        } catch (_) {
          // ignore
        }
      }
    }
  };

  const send = (roomType, roomId, event, payload) => {
    const room = `${roomType}:${roomId}`;
    const ws = socketsRef.current[room];
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ event, payload }));
    }
  };

  useEffect(() => () => {
    Object.values(reconnectTimersRef.current).forEach((timerId) => window.clearTimeout(timerId));
    Object.values(socketsRef.current).forEach((ws) => {
      try {
        ws.close();
      } catch (_) {
        // ignore
      }
    });
  }, []);

  return (
    <WebSocketContext.Provider value={{ connect, subscribe, unsubscribe, send }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
